import { GameConfig } from "@/config/GameConfig";
import type { Bite, BiteKind, Point } from "./types";
import type { RandomSource } from "./rng";
import { cellKey, pointsEqual } from "@/levels/geometry";

const BITE_KINDS: readonly BiteKind[] = ["safe", "rush", "chaos"];

/** Cell reachable from the head, tagged with its BFS distance (in steps). */
interface ReachableCell {
  point: Point;
  distance: number;
}

/**
 * Breadth-first search from the snake's head over every cell that is not
 * an obstacle, snake segment, or the exit gate. Only used to reject
 * spawning a bite somewhere completely cut off from the head right now -
 * it makes no claim about whether the player can *survive* to reach it.
 */
function findReachableCells(
  head: Point,
  cols: number,
  rows: number,
  blocked: ReadonlySet<string>,
): ReachableCell[] {
  const visited = new Set<string>();
  const result: ReachableCell[] = [];
  // The head is always "blocked" (the snake occupies it), but it's still
  // the BFS's starting point - it's just never a valid bite cell itself,
  // which the `blocked` check below already excludes from `result`.
  const queue: ReachableCell[] = [{ point: head, distance: 0 }];
  visited.add(cellKey(head));

  let cursor = 0;
  while (cursor < queue.length) {
    const current = queue[cursor];
    cursor++;
    if (!blocked.has(cellKey(current.point))) {
      result.push(current);
    }
    const neighbors: Point[] = [
      { x: current.point.x + 1, y: current.point.y },
      { x: current.point.x - 1, y: current.point.y },
      { x: current.point.x, y: current.point.y + 1 },
      { x: current.point.x, y: current.point.y - 1 },
    ];
    for (const neighbor of neighbors) {
      if (neighbor.x < 0 || neighbor.x >= cols || neighbor.y < 0 || neighbor.y >= rows) continue;
      const key = cellKey(neighbor);
      if (visited.has(key)) continue;
      visited.add(key);
      if (blocked.has(key)) continue; // don't expand through blocked cells
      queue.push({ point: neighbor, distance: current.distance + 1 });
    }
  }

  return result;
}

function pickDistinctRandom<T>(items: readonly T[], count: number, random: RandomSource): T[] {
  const pool = [...items];
  const picked: T[] = [];
  for (let i = 0; i < count && pool.length > 0; i++) {
    const index = Math.floor(random() * pool.length);
    picked.push(pool[index]);
    pool.splice(index, 1);
  }
  return picked;
}

function shuffle<T>(items: readonly T[], random: RandomSource): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Chooses 3 cells (one per bite kind) reachable from the snake's head,
 * distinct from each other and from the snake/obstacles/exit, and - best
 * effort - within `maxDistanceSpreadCells` of each other so the choice
 * between them is a real one. Bounded to `maxPlacementAttempts` tries so a
 * hard-to-satisfy board can never hang the game.
 */
/**
 * Builds the `count`-length list of kinds a fruit group spawns as, from
 * `allowed` (defaults to the full SAFE/RUSH/CHAOS trio - always exactly
 * that set for every campaign level, unchanged). `allowed` with fewer
 * than `count` kinds (Daily Challenge's `safe_only`/`rush_only`/
 * `chaos_only` bite modes - see `src/daily/types.ts`) cycles through them
 * to fill every slot, so e.g. `chaos_only` always spawns `count` CHAOS
 * fruits, never fewer - then shuffles the resulting multiset so which
 * physical cell gets which (possibly repeated) kind is still randomized.
 */
function buildKindsForSpawn(count: number, allowed: readonly BiteKind[], random: RandomSource): BiteKind[] {
  const kinds: BiteKind[] = [];
  for (let i = 0; i < count; i++) kinds.push(allowed[i % allowed.length]);
  return shuffle(kinds, random);
}

export function spawnBites(
  head: Point,
  cols: number,
  rows: number,
  blocked: ReadonlySet<string>,
  random: RandomSource,
  allowedBiteKinds: readonly BiteKind[] = BITE_KINDS,
): Bite[] {
  const reachable = findReachableCells(head, cols, rows, blocked);
  const count = GameConfig.bites.spawn.count;
  if (reachable.length < count) {
    // Degenerate board (shouldn't happen on any real level): place as many
    // as we can rather than nothing at all.
    const points = reachable.map((c) => c.point);
    const kinds = buildKindsForSpawn(points.length, allowedBiteKinds, random);
    return points.map((point, i) => ({ kind: kinds[i], point }));
  }

  const maxSpread = GameConfig.bites.spawn.maxDistanceSpreadCells;
  let chosen: ReachableCell[] | null = null;
  for (let attempt = 0; attempt < GameConfig.bites.spawn.maxPlacementAttempts; attempt++) {
    const candidate = pickDistinctRandom(reachable, count, random);
    const distances = candidate.map((c) => c.distance);
    const spread = Math.max(...distances) - Math.min(...distances);
    if (spread <= maxSpread) {
      chosen = candidate;
      break;
    }
  }

  if (!chosen) {
    // Fallback: any 3 valid, reachable, distinct cells - closest to the
    // head first, so the choice stays as fair as possible even here.
    chosen = [...reachable].sort((a, b) => a.distance - b.distance).slice(0, count);
  }

  const kinds = buildKindsForSpawn(count, allowedBiteKinds, random);
  return chosen.map((cell, i) => ({ kind: kinds[i], point: cell.point }));
}

/**
 * Best-effort placement of the legendary STARFRUIT bite alongside an
 * already-placed SAFE/RUSH/CHAOS trio - never the other way around. The
 * regular trio (`existingBites`) is never touched or re-rolled here: if no
 * legal, distinct, reachable cell for STARFRUIT can be found within
 * `maxPlacementAttempts`, this simply returns `null` and the caller keeps
 * the trio exactly as already placed - "must never make the three regular
 * fruits fail to spawn" holds by construction, since this function cannot
 * affect them either way. Bounded by the same config as the regular
 * placement search, so it can never hang the game.
 */
export function findStarfruitCell(
  head: Point,
  cols: number,
  rows: number,
  blocked: ReadonlySet<string>,
  existingBites: readonly Point[],
  random: RandomSource,
): Point | null {
  const fullyBlocked = new Set(blocked);
  for (const point of existingBites) fullyBlocked.add(cellKey(point));

  const reachable = findReachableCells(head, cols, rows, fullyBlocked);
  if (reachable.length === 0) return null;

  // Best-effort distance fairness: try to land within the same spread the
  // trio already used relative to the head, same bound as regular
  // placement - falls back to the closest reachable cell if no attempt
  // lands in range.
  const existingDistances = existingBites.map((p) => {
    const match = reachable.find((c) => pointsEqual(c.point, p));
    return match?.distance;
  });
  const maxSpread = GameConfig.bites.spawn.maxDistanceSpreadCells;

  for (let attempt = 0; attempt < GameConfig.bites.spawn.maxPlacementAttempts; attempt++) {
    const index = Math.floor(random() * reachable.length);
    const candidate = reachable[index];
    const withinSpread = existingDistances.every(
      (d) => d === undefined || Math.abs(d - candidate.distance) <= maxSpread,
    );
    if (withinSpread) return candidate.point;
  }

  return [...reachable].sort((a, b) => a.distance - b.distance)[0].point;
}
